(() => {
  const desktopViewport = window.matchMedia('(min-width: 990px)');
  const zoom = 2.4;

  class ProductImageMagnifier {
    constructor(gallery) {
      this.gallery = gallery;
      this.selection = document.createElement('div');
      this.selection.className = 'product-image-magnifier__selection';
      this.selection.setAttribute('aria-hidden', 'true');
      this.preview = document.createElement('div');
      this.preview.className = 'product-image-magnifier__preview';
      this.preview.setAttribute('aria-hidden', 'true');
      this.gallery.appendChild(this.preview);
      this.frame = null;
      this.pendingPointer = null;
      this.lastPointer = null;
      this.blockedPointer = null;
      this.isScrolling = false;
      this.requiresIntentionalMove = false;
      this.scrollTimer = null;
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onScrollStart = this.onScrollStart.bind(this);
      this.hide = this.hide.bind(this);

      gallery.addEventListener('pointermove', this.onPointerMove);
      gallery.addEventListener('pointerleave', this.hide);
      gallery.addEventListener('click', this.onClick.bind(this), true);
      window.addEventListener('wheel', this.onScrollStart, { passive: true, capture: true });
      window.addEventListener('scroll', this.onScrollStart, { passive: true });
    }

    getActiveImage() {
      return this.gallery.querySelector('.product__media-item.is-active .product__modal-opener--image img');
    }

    getHighResolutionSource(image) {
      const candidates = (image.srcset || '')
        .split(',')
        .map((candidate) => {
          const parts = candidate.trim().split(/\s+/);
          return { url: parts[0], width: Number.parseInt(parts[1], 10) || 0 };
        })
        .filter((candidate) => candidate.url);

      candidates.sort((a, b) => b.width - a.width);
      return candidates[0]?.url || image.currentSrc || image.src;
    }

    onClick(event) {
      if (event.target.closest('[data-target]')) {
        this.hide();
        return;
      }

      if (!desktopViewport.matches) return;
      const activeImage = this.getActiveImage();
      const opener = activeImage?.closest('.product__modal-opener--image');
      if (!opener?.contains(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
    }

    onPointerMove(event) {
      if (!desktopViewport.matches || event.pointerType === 'touch') return this.hide();

      const pointer = { clientX: event.clientX, clientY: event.clientY };
      if (this.isScrolling) {
        this.lastPointer = pointer;
        this.blockedPointer = pointer;
        return this.hide();
      }

      if (this.requiresIntentionalMove) {
        const reference = this.blockedPointer || this.lastPointer;
        const movement = reference ? Math.hypot(pointer.clientX - reference.clientX, pointer.clientY - reference.clientY) : 0;
        this.lastPointer = pointer;
        if (movement < 2) return this.hide();
        this.requiresIntentionalMove = false;
      } else {
        this.lastPointer = pointer;
      }

      const image = this.getActiveImage();
      const opener = image?.closest('.product__modal-opener--image');
      if (!image || !opener || !opener.contains(event.target)) return this.hide();

      this.pendingPointer = { clientX: event.clientX, clientY: event.clientY, image, opener };
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateZoom(this.pendingPointer);
      });
    }

    onScrollStart() {
      if (!desktopViewport.matches) return;
      this.isScrolling = true;
      this.requiresIntentionalMove = true;
      this.blockedPointer = this.lastPointer;
      this.hide();
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        this.isScrolling = false;
      }, 160);
    }

    updateZoom(pointer) {
      if (!pointer) return;
      const { clientX, clientY, image, opener } = pointer;
      const imageRect = image.getBoundingClientRect();
      const openerRect = opener.getBoundingClientRect();
      const naturalWidth = image.naturalWidth || imageRect.width;
      const naturalHeight = image.naturalHeight || imageRect.height;
      const imageRatio = naturalWidth / naturalHeight;
      const boxRatio = imageRect.width / imageRect.height;
      const objectFit = getComputedStyle(image).objectFit;
      let renderedWidth = imageRect.width;
      let renderedHeight = imageRect.height;

      if (objectFit === 'contain') {
        if (imageRatio > boxRatio) renderedHeight = renderedWidth / imageRatio;
        else renderedWidth = renderedHeight * imageRatio;
      } else if (objectFit === 'cover') {
        if (imageRatio > boxRatio) renderedWidth = renderedHeight * imageRatio;
        else renderedHeight = renderedWidth / imageRatio;
      }

      const renderedLeft = imageRect.left + (imageRect.width - renderedWidth) / 2;
      const renderedTop = imageRect.top + (imageRect.height - renderedHeight) / 2;
      const pointerX = clientX - renderedLeft;
      const pointerY = clientY - renderedTop;

      if (pointerX < 0 || pointerY < 0 || pointerX > renderedWidth || pointerY > renderedHeight) {
        return this.hide();
      }

      if (this.selection.parentElement !== opener) opener.appendChild(this.selection);

      const galleryRect = this.gallery.getBoundingClientRect();
      const availablePreviewSpace = window.innerWidth - galleryRect.right - 36;
      const previewSize = Math.min(520, Math.max(0, availablePreviewSpace));
      if (previewSize < 240) return this.hide();
      this.preview.style.width = `${previewSize}px`;
      this.preview.style.height = `${previewSize}px`;

      const previewWidth = previewSize;
      const previewHeight = previewSize;
      const selectionWidth = Math.min(renderedWidth, previewWidth / zoom);
      const selectionHeight = Math.min(renderedHeight, previewHeight / zoom);
      const centerX = Math.max(selectionWidth / 2, Math.min(renderedWidth - selectionWidth / 2, pointerX));
      const centerY = Math.max(selectionHeight / 2, Math.min(renderedHeight - selectionHeight / 2, pointerY));
      const selectionX = centerX - selectionWidth / 2;
      const selectionY = centerY - selectionHeight / 2;

      this.selection.style.width = `${selectionWidth}px`;
      this.selection.style.height = `${selectionHeight}px`;
      this.selection.style.left = `${renderedLeft - openerRect.left + selectionX}px`;
      this.selection.style.top = `${renderedTop - openerRect.top + selectionY}px`;

      this.preview.style.backgroundImage = `url("${this.getHighResolutionSource(image)}")`;
      this.preview.style.backgroundSize = `${renderedWidth * zoom}px ${renderedHeight * zoom}px`;
      this.preview.style.backgroundPosition = `${-selectionX * zoom}px ${-selectionY * zoom}px`;

      this.selection.classList.add('is-visible');
      this.preview.classList.add('is-visible');
    }

    hide() {
      this.pendingPointer = null;
      if (this.frame) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
      }
      this.selection.classList.remove('is-visible');
      this.preview.classList.remove('is-visible');
    }
  }

  const init = (root = document) => {
    root.querySelectorAll('.product-image-magnifier').forEach((gallery) => {
      if (gallery.dataset.magnifierReady) return;
      gallery.dataset.magnifierReady = 'true';
      new ProductImageMagnifier(gallery);
    });
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
